import { NavigateOptions, useSearchParams } from 'react-router-dom';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { getQueryParams, QueryParams } from './';

export const useQueryParams = <
  Params extends Partial<QueryParams> = Partial<QueryParams>
>(): [
  Partial<{
    [K in keyof Params]: Params[K] extends string ? Params[K] : string;
  }>,
  (
    params: Partial<{ [K in keyof Params]: Params[K] }>,
    options?: NavigateOptions
  ) => void
] => {
  const [searchParams, setSearchParams] = useSearchParams();
  const searchParamsRef = useRef(searchParams);

  // Working around a known issue: https://github.com/remix-run/react-router/issues/9991
  const setSearchParamsRef = useRef(setSearchParams);

  useEffect(() => {
    searchParamsRef.current = searchParams;
  }, [searchParams]);

  const queryParams = useMemo(
    () =>
      getQueryParams() as Partial<{
        [K in keyof Params]: Params[K] extends string ? Params[K] : string;
      }>,
    [searchParams]
  );

  const setQueryParams = useCallback(
    (
      params: Partial<{ [K in keyof Params]: Params[K] }>,
      options?: NavigateOptions
    ) => {
      const newSearchParams = new URLSearchParams(searchParamsRef.current);

      for (const [key, value] of Object.entries(params)) {
        if (typeof value === 'undefined') {
          newSearchParams.delete(key);
        } else {
          newSearchParams.set(key, value);
        }
      }

      if (newSearchParams.toString() !== searchParamsRef.current.toString()) {
        setSearchParamsRef.current(newSearchParams, options);
      }
    },
    []
  );

  return [queryParams, setQueryParams];
};
